import { initEmotionDictionary } from "./utils/util";
import { createStudentMaps } from "./utils/dataProcessors";
import ObjectID from "bson-objectid";

export async function getSuperadminData(user, wellness, anonamizeData) {
  try {
    const isSuperAdmin = user.role === "superadmin";

    // Load initial data in parallel
    const userDistrict = user.districts?.[0] ?? {};

    const allStudents = user?.allStudents ?? [];
    const allStudentIDs = allStudents.map((student) => student._id);

    if (allStudentIDs.length === 0) {
      return createEmptyResponse(userDistrict);
    }
    const { emailToName: studentEmailToNameMap } = createStudentMaps(
      allStudents,
      anonamizeData,
    );
    const { raw: allWellnesses, byUserId: rawStudentData } = wellness;

    let studentsClasses = {};
    if (user.role === "superadmin") {
      user.schools.forEach((school) => {
        studentsClasses[school.name] = {};
        // Track duplicate class names per school
        const classNameCount = new Map();
        school.classes.forEach((_class) => {
          let currentClass = _class;
          if (typeof _class === "string") {
            const classesMap = new Map();
            for (const c of user.classes) {
              if (ObjectID.isValid(_class)) {
                classesMap.set(c._id.toString(), c);
              } else {
                classesMap.set(c.name, c);
              }
            }
            if (classesMap.has(_class)) {
              currentClass = classesMap.get(_class);
            } else {
              return; // skip if we cannot resolve
            }
          }

          const baseName = currentClass?.name || "Unnamed Class";
          const count = classNameCount.get(baseName) || 0;
          classNameCount.set(baseName, count + 1);
          const finalClassName =
            count === 0 ? baseName : `${baseName} (${count})`;

          studentsClasses[school.name][finalClassName] = currentClass.students;
        });
      });
    } else if (user.role === "admin" || user.role === "teacher") {
      studentsClasses = groupClassesBySchool(user);
    }

    // logger.log("studentsClasses")
    // logger.log(studentsClasses)

    // Process student data
    const studentData = processStudentData(rawStudentData, allStudents);

    // Get school name from schoolsInfo if user is admin
    let schoolName = "Choose School";
    let defaultDistrict = "Choose District";
    let defaultClass = "Choose Class";

    // Set default selections based on user role
    if (user.role === "superadmin") {
      defaultDistrict = userDistrict.name;
      schoolName = "All Schools";
      defaultClass = "All Classes";
    } else if (user.role === "admin" || user.role === "teacher") {
      defaultDistrict = userDistrict.name;
      // Someone at more than one school starts on all of them, like a
      // superadmin does.
      const schoolNames = Object.keys(studentsClasses);
      if (schoolNames.length > 1) {
        schoolName = "All Schools";
      } else if (schoolNames.length === 1) {
        schoolName = schoolNames[0];
      }
      defaultClass = "All Classes";
    } else if (user.role === "123wellness") {
      defaultDistrict = "All Districts";
      schoolName = "All Schools";
      defaultClass = "All Classes";
    }

    // Structure districts data based on role
    const districtsData = isSuperAdmin
      ? { [user.districts?.[0]?.name]: studentsClasses }
      : { [user.districts?.[0]?.name]: studentsClasses || {} };

    return {
      studentEmailToNameMap,
      districts: districtsData,
      allWellnessPoints: allWellnesses,
      allStudents,
      studentData,
      currentDictionary: initEmotionDictionary(),
      compareDictionary: initEmotionDictionary(),
      loading: false,
      counter: 0,
      chosenDistrict: defaultDistrict,
      chosenSchool: schoolName,
      chosenClass: defaultClass,
    };
  } catch (error) {
    throw error;
  }
}

// Admins and teachers can be at more than one school, so each class goes under
// its own school: { [schoolName]: { [className]: studentEmails } }. Bootstrap
// sends a class's school as an id, resolved here against the user's schools.
// Repeated class names within a school get a " (n)" suffix so none is lost.
export function groupClassesBySchool(user) {
  const schoolNameById = new Map(
    (user.schools ?? []).map((school) => [String(school._id), school.name]),
  );
  const fallbackSchool = user.schools?.[0]?.name || "Unknown School";

  const bySchool = {};
  const classNameCountBySchool = {};
  for (const c of user.classes ?? []) {
    if (!c || !c.name) continue;

    const schoolName =
      schoolNameById.get(String(c.school?._id ?? c.school)) || fallbackSchool;
    bySchool[schoolName] ??= {};
    classNameCountBySchool[schoolName] ??= new Map();

    const counts = classNameCountBySchool[schoolName];
    const count = counts.get(c.name) || 0;
    counts.set(c.name, count + 1);
    const className = count === 0 ? c.name : `${c.name} (${count})`;

    bySchool[schoolName][className] = c.students ?? [];
  }
  return bySchool;
}

// Convert raw student data to email-based format
function processStudentData(rawStudentData, allStudents) {
  const studentData = {};
  Object.entries(rawStudentData).forEach(([userID, data]) => {
    const email = allStudents.find(
      (s) => s._id.toString() === userID.toString(),
    )?.email;
    if (email) {
      studentData[email] = data;
    }
  });
  return studentData;
}

function createEmptyResponse(districts) {
  return {
    studentEmailToNameMap: {},
    districts,
    allWellnessPoints: [],
    allStudents: [],
    studentData: {},
    currentDictionary: initEmotionDictionary(),
    compareDictionary: initEmotionDictionary(),
    loading: false,
    counter: 0,
    chosenDistrict: districts.length === 1 ? districts[0]?.name : "",
  };
}
